import React, { useMemo } from "react";
import { Elements } from "@stripe/react-stripe-js";
import type { StripeElementsOptions } from "@stripe/stripe-js";

import { getStripePromise } from "../../../helpers/stripeLoader";
import ExpressCheckoutButton, { ExpressCheckoutButtonProps } from "./index";

export interface ExpressCheckoutWrapperProps extends ExpressCheckoutButtonProps {
  /**
   * Amount in cents (e.g., 1099 for $10.99). Required for payment mode, ignored in setup mode.
   */
  amount?: number;
  /**
   * Currency code (e.g., "aud", "gbp", "usd")
   */
  currency: string;
  /**
   * Optional: Set to true when saving payment method for future use
   * This uses 'setup' mode instead of 'payment' mode
   */
  setupMode?: boolean;
}

const ExpressCheckoutWrapper = ({
  amount,
  currency,
  setupMode = false,
  ...buttonProps
}: ExpressCheckoutWrapperProps) => {
  // Loaded on mount, not at module scope: the wallet button is a progressive
  // enhancement, so a failed Stripe.js load just renders nothing and the next
  // mount retries (the loader clears its cache on failure).
  const stripePromise = useMemo(() => getStripePromise(), []);
  const elementsOptions: StripeElementsOptions = setupMode
    ? {
        mode: "setup",
        currency: currency.toLowerCase(),
        setupFutureUsage: "off_session",
      }
    : {
        mode: "payment",
        amount: amount as number,
        currency: currency.toLowerCase(),
        // Must match server's PaymentIntent setup_future_usage to save payment method
        setupFutureUsage: "off_session",
      };

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <ExpressCheckoutButton {...buttonProps} setupMode={setupMode} />
    </Elements>
  );
};

export default ExpressCheckoutWrapper;
